import type { FC } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Inbox,
  RefreshCw,
  Search,
} from 'lucide-react';
import type { InboxMessageSummary } from '@antigravity/types';

interface MessageListProps {
  messages: InboxMessageSummary[];
  selectedMessageId: string | null;
  onSelectMessage: (id: string) => void;
  isLoading: boolean;
  error?: string | null;
  onRefresh: () => void;
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (newPage: number) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSearchSubmit: () => void;
}

export const MessageList: FC<MessageListProps> = ({
  messages,
  selectedMessageId,
  onSelectMessage,
  isLoading,
  error,
  onRefresh,
  page,
  totalPages,
  total,
  onPageChange,
  searchQuery,
  onSearchChange,
  onSearchSubmit,
}) => {
  return (
    <div className="h-full flex flex-col bg-slate-900/60 border-r border-slate-800/80">
      {/* Search & Actions Header */}
      <div className="p-3.5 border-b border-slate-800/80 bg-slate-900/40 space-y-2.5">
        <div className="flex items-center gap-2">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSearchSubmit();
            }}
            className="flex-1 relative"
          >
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search subject or sender..."
              className="w-full pl-8 pr-3 py-1.5 rounded-lg bg-slate-800/80 border border-slate-700/60 text-xs text-white placeholder-slate-400 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors"
            />
          </form>

          <button
            type="button"
            onClick={onRefresh}
            disabled={isLoading}
            aria-label="Refresh messages"
            className="p-2 rounded-lg bg-slate-800/80 hover:bg-slate-700 text-slate-300 border border-slate-700/60 transition-colors disabled:opacity-50"
            title="Refresh inbox"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Counter & Pagination */}
        <div className="flex items-center justify-between text-[11px] text-slate-400 px-1">
          <span>
            {total} {total === 1 ? 'message' : 'messages'}
          </span>

          {totalPages > 1 && (
            <div className="flex items-center gap-1.5">
              <span>
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1 || isLoading}
                aria-label="Previous page"
                className="p-1 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-slate-300 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages || isLoading}
                aria-label="Next page"
                className="p-1 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-slate-300 transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Message Items List */}
      <div className="flex-1 overflow-y-auto divide-y divide-slate-800/50">
        {isLoading && messages.length === 0 ? (
          /* Skeletons */
          <div className="p-4 space-y-3 animate-pulse">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="p-3 bg-slate-800/40 rounded-xl space-y-2">
                <div className="flex justify-between">
                  <div className="h-3.5 bg-slate-700/60 rounded w-1/3" />
                  <div className="h-3 bg-slate-700/60 rounded w-16" />
                </div>
                <div className="h-3.5 bg-slate-700/60 rounded w-3/4" />
                <div className="h-3 bg-slate-700/40 rounded w-full" />
              </div>
            ))}
          </div>
        ) : error ? (
          /* Error State */
          <div className="p-8 text-center space-y-3">
            <p className="text-xs text-rose-400">{error}</p>
            <button
              type="button"
              onClick={onRefresh}
              className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs text-slate-200 border border-slate-700 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : messages.length === 0 ? (
          /* Empty State */
          <div className="p-12 text-center text-slate-400 space-y-2">
            <Inbox className="w-8 h-8 mx-auto text-slate-400" />
            <p className="text-xs font-medium text-slate-400">No messages found</p>
            <p className="text-[11px] text-slate-400">Your mailbox inbox is currently empty.</p>
          </div>
        ) : (
          /* Real Message Cards */
          messages.map((msg) => {
            const isSelected = selectedMessageId === msg.id;

            return (
              <button
                key={msg.id}
                type="button"
                onClick={() => onSelectMessage(msg.id)}
                className={`w-full text-left p-3.5 transition-colors relative flex items-start gap-3 ${
                  isSelected
                    ? 'bg-brand-600/15 border-l-2 border-brand-500'
                    : 'hover:bg-slate-800/40'
                }`}
              >
                {/* Read / Unread Indicator Dot */}
                <div className="pt-1">
                  {!msg.isRead ? (
                    <div
                      className="w-2 h-2 rounded-full bg-brand-500 shadow-sm shadow-brand-500/50"
                      title="Unread message"
                    />
                  ) : (
                    <div className="w-2 h-2" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span
                      className={`text-xs truncate ${
                        !msg.isRead ? 'font-bold text-white' : 'font-medium text-slate-300'
                      }`}
                    >
                      {msg.from || 'Unknown'}
                    </span>
                    <span className="text-[10px] text-slate-400 shrink-0">
                      {msg.date ? msg.date.split(' ').slice(0, 3).join(' ') : ''}
                    </span>
                  </div>

                  <div
                    className={`text-xs truncate mb-1 ${
                      !msg.isRead ? 'font-semibold text-slate-100' : 'text-slate-300'
                    }`}
                  >
                    {msg.subject || '(No Subject)'}
                  </div>

                  {msg.preview && (
                    <p className="text-[11px] text-slate-400 truncate line-clamp-1">
                      {msg.preview}
                    </p>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};
